if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.mediaIndex = 0;
        this.imageZoom = 1;
        this.mediaGestures = null;
        this.setupMediaControls();
      }

      hide() {
        this.resetZoom();
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
      }

      setupMediaControls() {
        const content = this.querySelector('.product-media-modal__content');
        const toolbar = this.querySelector('.product-media-modal__toolbar');

        content?.addEventListener('pointerup', (event) => event.stopPropagation());
        toolbar?.addEventListener('pointerup', (event) => event.stopPropagation());
        this.querySelectorAll('.product-media-modal__nav').forEach((button) => {
          button.addEventListener('pointerup', (event) => event.stopPropagation());
        });

        this.querySelector('[data-media-modal-prev]')?.addEventListener('click', () => this.stepMedia(-1));
        this.querySelector('[data-media-modal-next]')?.addEventListener('click', () => this.stepMedia(1));
        this.querySelector('[data-media-zoom-out]')?.addEventListener('click', () => this.setZoom(this.imageZoom - 0.25));
        this.querySelector('[data-media-zoom-in]')?.addEventListener('click', () => this.setZoom(this.imageZoom + 0.25));
        this.querySelector('[data-media-zoom-reset]')?.addEventListener('click', () => this.setZoom(1));

        this.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.stepMedia(-1);
          }
          if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.stepMedia(1);
          }
        });

        content?.addEventListener('click', (event) => {
          const image = event.target.closest('img[data-media-id]');
          if (!image || this.mediaGestures?.wasRecentlyDragged()) return;
          event.preventDefault();
          this.setZoom(this.imageZoom > 1 ? 1 : 2);
        });

        if (content && window.KickisFullscreenImageGestures) {
          this.mediaGestures = new window.KickisFullscreenImageGestures({
            root: this,
            surface: content,
            surfaceSelector: '.product-media-modal__content',
            imageSelector: 'img[data-media-id]',
            getImage: () => this.getActiveImage(),
            navigate: (direction) => this.stepMedia(direction),
            canNavigate: (direction) => {
              const count = this.activeMediaCount || 0;
              return direction < 0 ? this.mediaIndex > 0 : this.mediaIndex < count - 1;
            },
            close: () => this.hide(),
            preloadAdjacent: () => this.preloadAdjacentMedia(),
            onZoomChange: (zoom) => {
              this.imageZoom = zoom;
              content.toggleAttribute('data-zoomed', zoom > 1.01);
              this.updateZoomControls();
            },
          });
        }
      }

      preloadAdjacentMedia() {
        const items = this.getActiveVariantMediaItems();
        [items[this.mediaIndex - 1], items[this.mediaIndex + 1]].filter(Boolean).forEach((item) => {
          this.getItemImages(item).forEach((sourceImage) => {
            if (!sourceImage.currentSrc && !sourceImage.src) return;
            const preload = new Image();
            preload.src = sourceImage.currentSrc || sourceImage.src;
          });
        });
      }

      normalizeMediaId(value) {
        return String(value || '').split('-').pop();
      }

      getModalMediaItems() {
        const content = this.querySelector('.product-media-modal__content');
        if (!content) return [];

        return [...content.children]
          .map((element) => {
            const mediaElement = element.matches('[data-media-id]') ? element : element.querySelector('[data-media-id]');
            return mediaElement
              ? { element, mediaElement, id: this.normalizeMediaId(mediaElement.dataset.mediaId) }
              : null;
          })
          .filter(Boolean);
      }

      getVisibleGalleryMediaIds() {
        const gallery = this.openedBy?.closest('media-gallery');
        if (!gallery) return [];

        return [...gallery.querySelectorAll('[id^="Slider-Gallery-"] > li[data-media-id]')]
          .filter(
            (element) =>
              !element.hidden &&
              !element.classList.contains('hidden') &&
              window.getComputedStyle(element).display !== 'none'
          )
          .map((element) => this.normalizeMediaId(element.dataset.mediaId));
      }

      getActiveVariantMediaItems() {
        const allItems = this.getModalMediaItems();
        const visibleIds = new Set(this.getVisibleGalleryMediaIds());
        if (!visibleIds.size) return allItems;

        const filteredItems = allItems.filter((item) => visibleIds.has(item.id));
        return filteredItems.length ? filteredItems : allItems;
      }

      showActiveMedia() {
        const content = this.querySelector('.product-media-modal__content');
        const template = content?.querySelector('template[data-kickis-zoom]');
        if (template) template.replaceWith(template.content.cloneNode(true));
        const items = this.getActiveVariantMediaItems();
        const openerId = this.normalizeMediaId(this.openedBy?.getAttribute('data-media-id'));
        const requestedIndex = items.findIndex((item) => item.id === openerId);
        this.mediaIndex = requestedIndex >= 0 ? requestedIndex : 0;
        this.renderMedia();
      }

      stepMedia(direction) {
        const items = this.getActiveVariantMediaItems();
        if (!items.length) return;

        const nextIndex = Math.min(items.length - 1, Math.max(0, this.mediaIndex + direction));
        if (nextIndex === this.mediaIndex) {
          this.updateControls(items.length);
          return;
        }
        this.mediaIndex = nextIndex;
        this.renderMedia();
      }

      renderMedia() {
        const allItems = this.getModalMediaItems();
        const allowedItems = this.getActiveVariantMediaItems();
        const activeItem = allowedItems[this.mediaIndex] || allowedItems[0];
        const activeElement = activeItem?.element;
        this.activeMediaCount = allowedItems.length;
        this.mediaIndex = Math.max(0, allowedItems.indexOf(activeItem));

        allItems.forEach((item) => {
          const isActive = item.element === activeElement;
          item.element.hidden = !isActive;
          item.element.classList.toggle('active', isActive);
          item.element.classList.toggle('is-modal-active', isActive);
          item.element.setAttribute('aria-hidden', isActive ? 'false' : 'true');
          this.getItemImages(item).forEach((image) => {
            image.style.transform = 'scale(1)';
            image.style.transformOrigin = 'center center';
            image.style.transition = '';
            image.style.opacity = '';
          });
        });

        this.activeImage = activeElement?.matches('img[data-media-id]')
          ? activeElement
          : activeElement?.querySelector('img[data-media-id]') || null;
        this.imageZoom = 1;
        this.mediaGestures?.reset({ animate: false });
        this.updateControls(allowedItems.length);
        this.querySelector('.product-media-modal__content')?.scrollTo({ top: 0, left: 0 });

        if (activeItem?.mediaElement.nodeName === 'DEFERRED-MEDIA') activeItem.mediaElement.loadContent?.();
      }

      getActiveImage() {
        return this.activeImage || null;
      }

      getItemImages(item) {
        if (item.element.matches('img')) return [item.element];
        return [...item.element.querySelectorAll('img')];
      }

      setZoom(zoom) {
        if (this.mediaGestures) {
          this.mediaGestures.setZoom(zoom, { animate: true, preservePan: true });
          return;
        }
        const image = this.getActiveImage();
        if (!image) return;

        this.imageZoom = Math.min(4, Math.max(1, Math.round(zoom * 100) / 100));
        image.style.transform = `scale(${this.imageZoom})`;
        image.style.transformOrigin = 'center center';
        image.classList.toggle('is-zoomed', this.imageZoom > 1);
        this.querySelector('.product-media-modal__content')?.toggleAttribute('data-zoomed', this.imageZoom > 1);
        this.updateZoomControls();
      }

      resetZoom() {
        if (this.mediaGestures) {
          this.mediaGestures.reset({ animate: false });
          return;
        }
        this.getModalMediaItems().forEach((item) => {
          this.getItemImages(item).forEach((image) => {
            image.style.transform = 'scale(1)';
            image.classList.remove('is-zoomed');
          });
        });
        this.imageZoom = 1;
        this.querySelector('.product-media-modal__content')?.removeAttribute('data-zoomed');
        this.updateZoomControls();
      }

      updateControls(itemCount) {
        const previous = this.querySelector('[data-media-modal-prev]');
        const next = this.querySelector('[data-media-modal-next]');
        const counter = this.querySelector('[data-media-modal-counter]');
        const hasSeveralImages = itemCount > 1;

        if (previous) {
          previous.hidden = !hasSeveralImages;
          previous.disabled = this.mediaIndex <= 0;
        }
        if (next) {
          next.hidden = !hasSeveralImages;
          next.disabled = this.mediaIndex >= itemCount - 1;
        }
        if (counter) {
          counter.hidden = !hasSeveralImages;
          counter.textContent = itemCount ? `${this.mediaIndex + 1} / ${itemCount}` : '';
        }
        this.updateZoomControls();
      }

      updateZoomControls() {
        const status = this.querySelector('[data-media-zoom-status]');
        const zoomOut = this.querySelector('[data-media-zoom-out]');
        const zoomIn = this.querySelector('[data-media-zoom-in]');
        if (status) status.textContent = `${Math.round(this.imageZoom * 100)} %`;
        if (zoomOut) zoomOut.disabled = this.imageZoom <= 1;
        if (zoomIn) zoomIn.disabled = this.imageZoom >= 4;
      }
    }
  );
}
